import { CustomHeader } from '@/components/molecules/customHeader';
import { DimensionHighlight } from '@/components/molecules/dimensionHighlight';
import { PageLayout } from '@/components/molecules/pageLayout';
import { Municipality } from '@/components/organisms/municipality';
import { GeneralTab } from '@/components/organisms/municipality/tabs/general';
import { GenericTab } from '@/components/organisms/municipality/tabs/generic';
import { AppRouters } from '@/utils/constants/routers';
import { QUERY_DIMENSION_VARIABLES } from '@/graphql/queries/dimensions';
import {
  QUERY_MUNICIPALITIES_COMPARATIVE,
  QUERY_MUNICIPALITY_PAGE,
  QUERY_MUNICIPALITY_COMPARATIVE,
  QUERY_MUNICIPALITY_DIMENSION_HIGHLIGHTS,
} from '@/graphql/queries/municipalities';
import {
  MunicipalityComparative,
  MunicipalitiesComparative,
  MunicipalityDimensionHighlights,
  MunicipalityWithDimensions,
} from '@/graphql/types/municipality';
import { MaxContent } from '@/styles/globalStyles';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  ArrowBack,
  BarChart,
  PictureAsPdfOutlined,
  TableViewOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { ElementType, ReactElement, ReactNode } from 'react';
import { DimensionVariable } from '@/graphql/types/dimensions';
import { Box, Skeleton, SvgIconProps, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { icon } from '@/utils/functions/tabIcons';
import { sort } from '@/utils/functions/sort';
import { DimensionAbout } from '@/components/molecules/dimensionAbout';
import { MUTATION_GENERATE_COMPOSITE_ANALYSIS_PDF } from '@/graphql/mutation/pdf';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/hooks/useLanguage';
import { useDownloadUsage } from '@/hooks/useDownloadUsage';
import {
  ClassificationOpportunityAndRisk as ClassificationOpportunityAndRiskType,
  ClassificationOpportunityAndRisk,
} from '@/types/classification';
import { generateInsightText } from '@/utils/functions/insights';
import { StyledBackButtonContainer, StyledMaxContent } from './styles';
import { Button } from '@/components/atoms/Button';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { CustomTabs } from '@/components/molecules/tabs/custom';
import { useQueryState } from 'nuqs';
import {
  ComparativeModal,
  MunicipalityComparativeModal,
} from '@/components/organisms/municipality/compare/modal';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { MunicipalityCompareBottomSheet } from '@/components/organisms/municipality/compare/bottomsheet';
import type { SheetRef } from 'react-modal-sheet';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import type { Municipality as MunicipalityType } from '@/components/molecules/dimensionVariableComparative';
import { MUTATION_GENERATE_MUNICIPALITY_XLSX } from '@/graphql/mutation/xlsx';
import { polygonDatabaseService } from '@/services/indexedDB/polygonDatabase';
import { Disclaimer } from '@/components/molecules/disclaimer';
import { MUNICIPALITY_COMPARISON_LIMIT } from '@/config/municipalityComparisonLimit';

export interface TabItem {
  note?: number;
  icon: ElementType<SvgIconProps>;
  label: string;
  value: string | number;
  slug: string;
  tab: ReactNode;
  children?: ReactNode;
}

export default function MunicipalityPage() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { cdMun } = useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { loading: languageLoading } = useLanguage();

  const bottomSheetRef = useRef<SheetRef | null>(null);

  const [openComparative, setOpenComparative] = useState(false);
  const [tabSlug, setTabSlug] = useQueryState('tab', {
    defaultValue: 'overall_score',
  });
  const [tabValue, setTabValue] = useState<string | number>(0);
  const [municipalitiesComparative, setMunicipalitiesComparative] = useState<
    MunicipalityComparativeModal[]
  >([]);
  const [dimensionVariables, setDimensionVariables] = useState<{
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  }>();
  const [dimensionVariablesComparative, setDimensionVariablesComparative] = useState<{
    municipalities: MunicipalityType[];
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  }>();
  const [dimensionVariablesLoading, setDimensionVariablesLoading] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingXLSX, setIsGeneratingXLSX] = useState(false);
  const [insightText, setInsightText] = useState<string>();
  const [municipalityData, setMunicipalityData] = useState<MunicipalityWithDimensions>();
  const [municipalityLoading, setMunicipalityLoading] = useState(false);
  const [
    municipalityDimensionHighlights,
    setMunicipalityDimensionHighlights,
  ] = useState<MunicipalityDimensionHighlights>();
  const [
    municipalityDimensionHighlightsLoading,
    setMunicipalityDimensionHighlightsLoading,
  ] = useState(false);
  const [municipalityCode, setMunicipalityCode] = useState<string>();

  const municipalitiesComparativeIds = Array.from(
    new Set(
      [cdMun, ...municipalitiesComparative.map((municipality) => municipality.id)].filter(
        (id): id is string => id !== null
      )
    )
  );

  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const { downloadUsage, refetch: refetchDownloadUsage } = useDownloadUsage();

  const customOrder = [1, 2, 3, 4, 5, 6, 7, 8];

  const orderedDimensionScores = sort.sortDimensions(
    municipalityData?.dimensions?.filter((item) => customOrder.includes(item.dimension.order)) ||
      [],
    customOrder
  );

  const byOpportunityAndRisk = (
    classification: ClassificationOpportunityAndRiskType,
    variant: 'opportunity' | 'risk'
  ) => {
    switch (classification) {
      case ClassificationOpportunityAndRisk.LOW:
        return variant === 'opportunity'
          ? t('label.label.low_opportunity')
          : t('label.label.low_risk');
      case ClassificationOpportunityAndRisk.MEDIUM_LOW:
        return variant === 'opportunity'
          ? t('label.label.medium_low_opportunity')
          : t('label.label.medium_low_risk');
      case ClassificationOpportunityAndRisk.MEDIUM_HIGH:
        return variant === 'opportunity'
          ? t('label.label.medium_high_opportunity')
          : t('label.label.medium_high_risk');
      default:
        return variant === 'opportunity'
          ? t('label.label.high_opportunity')
          : t('label.label.high_risk');
    }
  };

  useEffect(() => {
    if (!cdMun) {
      navigate(AppRouters.home);
    }
  }, [cdMun, navigate]);

  useEffect(() => {
    setMunicipalitiesComparative([]);
  }, [cdMun]);

  useEffect(() => {
    if (tabSlug && municipalityData) {
      const allTabs = [
        { value: 0, slug: 'overall_score' },
        ...orderedDimensionScores.map((item) => ({
          value: item.dimension.id,
          slug: item.dimension.bigquery_id,
        })),
      ];

      const matchingTab = allTabs.find((tab) => tab.slug === tabSlug);
      if (matchingTab) {
        setTabValue(matchingTab.value);
      }
    }
  }, [tabSlug, municipalityData, orderedDimensionScores]);

  const handleBack = () => {
    const urlFilter = sessionStorage.getItem('URL_FILTER');
    const parsedUrl = urlFilter ? JSON.parse(urlFilter) : null;
    if (parsedUrl) {
      navigate(-1);
    } else {
      sessionStorage.removeItem('URL_FILTER');
      navigate(AppRouters.home, { replace: true });
    }
  };

  const handleGetMunicipalitiesComparative = (municipalities: MunicipalityComparativeModal[]) => {
    setMunicipalitiesComparative(municipalities);
  };

  const handleOpenComparativeModal = () => {
    setOpenComparative(true);
  };

  const handleCloseComparativeModal = () => {
    setOpenComparative(false);
  };

  const handleGetTabValue = (tab: TabItem) => {
    setTabValue(tab.value);
    setTabSlug(tab.slug);
  };

  const handleGenerateCompositeAnalysisPDF = async (type: 'default' | 'compose') => {
    setIsGeneratingPDF(true);
    try {
      const { data } = await apolloClient.mutate<{
        generateCompositeAnalysisPDF: {
          url: string;
        };
      }>({
        mutation: MUTATION_GENERATE_COMPOSITE_ANALYSIS_PDF,
        variables: {
          language: currentLanguage,
          municipalityCode: cdMun,
          comparativeMunicipalitycodes:
            type === 'default' ? [] : municipalitiesComparativeIds.filter((item) => item != cdMun),
        },
      });

      if (data?.generateCompositeAnalysisPDF.url) {
        window.open(data.generateCompositeAnalysisPDF.url, '_blank');
        await refetchDownloadUsage();
      }
    } catch (error) {
      showAlert('error', t('messages.error.error.municipality.generatePDF'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleGenerateCompositeAnalysisXLSX = async (type: 'default' | 'compose') => {
    setIsGeneratingXLSX(true);
    try {
      const { data } = await apolloClient.mutate<{
        generateMunicipalityXLSX: {
          url: string;
        };
      }>({
        mutation: MUTATION_GENERATE_MUNICIPALITY_XLSX,
        variables: {
          language: currentLanguage,
          municipalityCode: cdMun,
          comparativeMunicipalityCodes:
            type === 'default' ? [] : municipalitiesComparativeIds.filter((item) => item != cdMun),
        },
      });

      if (data?.generateMunicipalityXLSX.url) {
        window.open(data.generateMunicipalityXLSX.url, '_blank');
        await refetchDownloadUsage();
      }
    } catch (error) {
      showAlert('error', t('messages.error.error.municipality.generateXLSX'));
    } finally {
      setIsGeneratingXLSX(false);
    }
  };

  const handleInsight = (dimensionId?: number) => {
    const comparedMunicipalitiesNames = municipalitiesComparative
      .filter((m): m is MunicipalityComparativeModal & { name: string } => Boolean(m.id && m.name))
      .map((m) => m.name)
      .filter((name) => name.trim() !== '');

    const insightText = generateInsightText({
      dimensionId,
      municipalityName: municipalityData?.municipality.name_municipality ?? '',
      stateName: municipalityData?.municipality.name_uf ?? '',
      language: currentLanguage,
      comparedMunicipalities: comparedMunicipalitiesNames,
    });
    setInsightText(insightText);
  };

  const handleResetInsight = () => {
    setInsightText(undefined);
  };

  const {
    data: municipalitiesComparativeData,
    loading: municipalitiesComparativeLoading,
  } = useQuery<{
    municipalitiesComparative: MunicipalitiesComparative[];
  }>(QUERY_MUNICIPALITIES_COMPARATIVE, {
    variables: {
      language: currentLanguage,
      municipalitiesCodes: municipalitiesComparativeIds,
      dimensionId: tabValue,
    },
    skip: municipalitiesComparativeIds.length === 0,
  });

  const dimensionHighlights = municipalityDimensionHighlights;

  const topOpportunities = dimensionHighlights?.highlights.topOpportunities || [];
  const topRisks = dimensionHighlights?.highlights.topRisks || [];

  const opportunityVariables = dimensionVariables?.opportunityVariables ?? [];
  const riskVariables = dimensionVariables?.riskVariables ?? [];

  const opportunityVariablesComparative = dimensionVariablesComparative?.opportunityVariables ?? [];
  const riskVariablesComparative = dimensionVariablesComparative?.riskVariables ?? [];
  const municipalitiesVariablesComparative = dimensionVariablesComparative?.municipalities ?? [];

  const { data: municipalityComparativeData, loading: municipalityComparativeLoading } = useQuery<{
    municipalityComparative: MunicipalityComparative[];
  }>(QUERY_MUNICIPALITY_COMPARATIVE, {
    variables: {
      dimensionId: tabValue === 0 ? undefined : tabValue,
      municipalityCode: cdMun,
      language: currentLanguage,
    },
    skip: !cdMun,
  });

  const comparativeData = municipalityComparativeData?.municipalityComparative;

  const isLoading =
    dimensionVariablesLoading || municipalityComparativeLoading || municipalitiesComparativeLoading;

  const dimensionHighlightsLoading = municipalityDimensionHighlightsLoading || isLoading;

  useEffect(() => {
    const getMunicipalityData = async () => {
      setMunicipalityLoading(true);
      try {
        const { data } = await apolloClient.query<{
          municipalityPageByCode: MunicipalityWithDimensions;
        }>({
          query: QUERY_MUNICIPALITY_PAGE,
          variables: {
            code: municipalityCode || cdMun,
            language: currentLanguage,
          },
        });

        const municipality = data.municipalityPageByCode.municipality;
        const polygon = await polygonDatabaseService.getPolygonByCode(
          municipality.municipality_code
        );

        setMunicipalityData({
          ...data.municipalityPageByCode,
          municipality: {
            ...municipality,
            geometry: polygon?.geometry,
          },
        });
      } finally {
        setMunicipalityLoading(false);
      }
    };
    getMunicipalityData();
  }, [cdMun, currentLanguage, municipalityCode]);

  useEffect(() => {
    const getMunicipalityDimensionHighlights = async () => {
      setMunicipalityDimensionHighlightsLoading(true);
      try {
        const { data } = await apolloClient.query<{
          municipalityDimensionHighlights: MunicipalityDimensionHighlights;
        }>({
          query: QUERY_MUNICIPALITY_DIMENSION_HIGHLIGHTS,
          variables: {
            municipalityCode: cdMun,
            language: currentLanguage,
          },
        });

        setMunicipalityDimensionHighlights(data.municipalityDimensionHighlights);
      } catch (error) {
        console.error('QUERY_MUNICIPALITY_DIMENSION_HIGHLIGHTS failed:', error);
        setMunicipalityDimensionHighlightsLoading(false);
      } finally {
        setMunicipalityDimensionHighlightsLoading(false);
      }
    };

    if (cdMun) {
      getMunicipalityDimensionHighlights();
    }
  }, [cdMun, currentLanguage]);

  const getDimensionVariables = async (municipalityCodes?: (string | undefined)[]) => {
    const { data } = await apolloClient.query<{
      dimensionVariables: {
        municipalities: MunicipalityType[];
        opportunity_variables: DimensionVariable[];
        risk_variables: DimensionVariable[];
      };
    }>({
      query: QUERY_DIMENSION_VARIABLES,
      variables: {
        dimensionId: tabValue,
        municipalityCode: cdMun,
        language: currentLanguage,
        ...(municipalityCodes && { municipalityCodes }),
      },
    });
    return data.dimensionVariables;
  };

  useEffect(() => {
    const fetchDimensionVariables = async () => {
      setDimensionVariablesLoading(true);
      try {
        if (municipalitiesComparativeIds.length > 0) {
          const data = await getDimensionVariables(municipalitiesComparativeIds);
          setDimensionVariablesComparative({
            municipalities: data.municipalities,
            opportunityVariables: data.opportunity_variables,
            riskVariables: data.risk_variables,
          });
        }

        const data = await getDimensionVariables();
        setDimensionVariables({
          opportunityVariables: data.opportunity_variables,
          riskVariables: data.risk_variables,
        });
      } catch (error) {
        setDimensionVariablesLoading(false);
      } finally {
        setDimensionVariablesLoading(false);
      }
    };
    fetchDimensionVariables();
  }, [cdMun, currentLanguage, tabValue, municipalitiesComparativeIds.length]);

  useEffect(() => {
    if (municipalityData?.municipality.name_municipality) {
      document.title = `KYT-e - ${municipalityData?.municipality.name_municipality}`;
    } else {
      document.title = 'KYT-e | Green Bridge Facility';
    }
  }, [municipalityData]);

  const dimensionsTabs =
    orderedDimensionScores
      .map((item) => {
        return {
          label: item.dimension.name,
          value: item.dimension.id,
          slug: item.dimension.bigquery_id,
          tab: (
            <GenericTab
              dimensionVariablesComparative={{
                municipalities: municipalitiesVariablesComparative,
                opportunityVariables: opportunityVariablesComparative,
                riskVariables: riskVariablesComparative,
              }}
              municipalityVsStateData={{
                data: comparativeData ?? [],
                onInsight: () => handleInsight(item.dimension.id),
              }}
              loadingPDF={isGeneratingPDF}
              loadingXLSX={isGeneratingXLSX}
              comparativeData={{
                data: municipalitiesComparativeData?.municipalitiesComparative ?? [],
                onInsight: () => handleInsight(100 + item.dimension.id),
                onCompare: handleOpenComparativeModal,
                isGeneratingPDF: isGeneratingPDF,
                downloadOptions: [
                  {
                    icon: PictureAsPdfOutlined,
                    label: t('button.button.export.pdf'),
                    onClick: () => handleGenerateCompositeAnalysisPDF('compose'),
                  },
                  {
                    icon: TableViewOutlined,
                    label: t('button.button.export.xlsx'),
                    onClick: () => handleGenerateCompositeAnalysisXLSX('compose'),
                    isLimited: true,
                  },
                ],
              }}
              loadingData={isLoading}
              tableData={{ opportunityVariables, riskVariables }}
              downloadUsage={downloadUsage}
              limitReachedMessage={t('button.button.download_limit_reached')}
              comparisonMapDimensionId={item.dimension.id}
              comparisonMapBigqueryId={item.dimension.bigquery_id}
              comparisonMapDimensionName={item.dimension.name}
            />
          ),
          icon: icon.getDimensionIcon(item.dimension.icon || ''),
          children: (
            <>
              {isLoading ? (
                <Skeleton width="100%" height={110} animation="wave" sx={{ transform: 'none' }} />
              ) : (
                <DimensionAbout
                  icon={icon.getDimensionIcon(item.dimension.icon || '')}
                  title={item.dimension.name}
                  description={item.dimension.description}
                />
              )}
            </>
          ),
        };
      })
      .filter(
        (
          tab
        ): tab is {
          slug: string;
          label: string;
          value: number;
          tab: ReactElement;
          icon: ElementType<SvgIconProps>;
          children: ReactElement;
        } => tab !== null
      ) ?? [];

  return (
    <>
      <PageLayout.Root
        header={
          <CustomHeader
            headerTitle={() => ({
              title: t('page.dashboard.dashboard.title'),
            })}
            loading={isGeneratingPDF || isGeneratingXLSX}
            downloadOptions={[
              {
                icon: PictureAsPdfOutlined,
                label: t('button.button.export.pdf'),
                onClick: () => handleGenerateCompositeAnalysisPDF('default'),
              },
              {
                icon: TableViewOutlined,
                label: t('button.button.export.xlsx'),
                onClick: () => handleGenerateCompositeAnalysisXLSX('default'),
                isLimited: true,
              },
            ]}
            downloadUsage={downloadUsage}
            limitReachedMessage={t('button.button.download_limit_reached')}
          />
        }
      >
        <StyledBackButtonContainer>
          <MaxContent>
            {languageLoading ? (
              <Skeleton width="130px" height={36} animation="wave" sx={{ transform: 'none' }} />
            ) : (
              <Button
                variant="text"
                startIcon={<ArrowBack color="action" />}
                onClick={handleBack}
                sx={{
                  color: 'inherit',
                  padding: theme.spacing(1),
                  height: 'fit-content',
                }}
              >
                <SafeMarkdown text={t('button.button.back_to_search')} />
              </Button>
            )}
          </MaxContent>
        </StyledBackButtonContainer>

        <StyledMaxContent>
          <Municipality
            cdMun={cdMun}
            municipalityData={municipalityData}
            onMunicipalityChange={setMunicipalityCode}
            municipalityLoading={municipalityLoading}
            insightText={insightText}
            onResetInsightText={handleResetInsight}
            tabs={
              <CustomTabs
                initialValue={tabValue}
                onGetTabValue={handleGetTabValue}
                loading={municipalityLoading}
                showBackground
                tabs={[
                  {
                    label: t('label.label.overall_score'),
                    value: 0,
                    slug: 'overall_score',
                    tab: (
                      <GeneralTab
                        names={{
                          municipality: municipalityData?.municipality.name_municipality ?? '',
                          state: municipalityData?.municipality.name_uf ?? '',
                        }}
                        comparativeData={{
                          data: municipalitiesComparativeData?.municipalitiesComparative ?? [],
                          onInsight: () => handleInsight(100),
                          onCompare: handleOpenComparativeModal,
                        }}
                        loadingData={isLoading}
                        loadingPDF={isGeneratingPDF}
                        loadingXLSX={isGeneratingXLSX}
                        downloadOptions={[
                          {
                            icon: PictureAsPdfOutlined,
                            label: t('button.button.export.pdf'),
                            onClick: () => handleGenerateCompositeAnalysisPDF('compose'),
                          },
                          {
                            icon: TableViewOutlined,
                            label: t('button.button.export.xlsx'),
                            onClick: () => handleGenerateCompositeAnalysisXLSX('compose'),
                            isLimited: true,
                          },
                        ]}
                        downloadUsage={downloadUsage}
                        limitReachedMessage={t('button.button.download_limit_reached')}
                      />
                    ),
                    icon: BarChart,
                    children: (
                      <>
                        {dimensionHighlightsLoading ? (
                          <Skeleton
                            width="100%"
                            height={286}
                            animation="wave"
                            sx={{ transform: 'none' }}
                          />
                        ) : (
                          <DimensionHighlight
                            opportunity={topOpportunities.map((item) => ({
                              name: item.dimension.name,
                              value: byOpportunityAndRisk(item.classification, 'opportunity'),
                            }))}
                            risk={topRisks.map((item) => ({
                              name: item.dimension.name,
                              value: byOpportunityAndRisk(item.classification, 'risk'),
                            }))}
                          />
                        )}
                      </>
                    ),
                  },
                  ...dimensionsTabs,
                ]}
              />
            }
          />

          <Box
            sx={{
              paddingBottom: theme.spacing(isTablet ? 15 : 3),
              paddingX: isTablet ? theme.spacing(3) : 0,
              backgroundColor: isTablet ? theme.palette.common.white : 'transparent',
            }}
          >
            <Disclaimer
              icon={WarningAmberOutlined}
              text="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum."
              // showMoreButton
            />
          </Box>
        </StyledMaxContent>
      </PageLayout.Root>

      {openComparative && (
        <>
          <Mobile>
            <MunicipalityCompareBottomSheet
              open={openComparative}
              onClose={handleCloseComparativeModal}
              municipalityData={{
                id: municipalityData?.municipality.municipality_code,
                name: municipalityData?.municipality.name_municipality,
              }}
              onGetMunicipalitiesComparative={handleGetMunicipalitiesComparative}
              savedSelections={municipalitiesComparative}
              limit={MUNICIPALITY_COMPARISON_LIMIT}
              bottomSheetRef={bottomSheetRef}
            />
          </Mobile>

          <Desktop>
            <ComparativeModal
              municipalityData={{
                id: municipalityData?.municipality.municipality_code,
                name: municipalityData?.municipality.name_municipality,
              }}
              open={openComparative}
              onClose={handleCloseComparativeModal}
              onGetMunicipalitiesComparative={handleGetMunicipalitiesComparative}
              savedSelections={municipalitiesComparative}
              limit={MUNICIPALITY_COMPARISON_LIMIT}
            />
          </Desktop>
        </>
      )}

      {alert.open && (
        <Alert
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          autoHideDuration={5000}
          onClose={handleCloseAlert}
        />
      )}
    </>
  );
}
